import vivarealLogo from '@/public/images/importer/vivareal.png';
import imovelwebLogo from '@/public/images/importer/imovelweb.png';
import casaMineiraLogo from '@/public/images/importer/casa-mineira.svg';
import dfImoveisLogo from '@/public/images/importer/df-imoveis.png';
import urbaniaLogo from '@/public/images/importer/urbania.svg';

// Portal origin cards always start a `properties` import (Figma 511-26849).
// `developments` remains a valid API entity for historical jobs and future
// wizard work, but this iteration removes entity selection — OpenNavent API
// portals therefore send `entity=properties` until product reintroduces
// developments in the wizard.

export const API_METHOD_KEY = 'ImporterWizardMethodApi';
export const XML_METHOD_KEY = 'ImporterWizardMethodXmlFile';
export const SQL_METHOD_KEY = 'ImporterWizardMethodSqlFile';
export const EXCEL_METHOD_KEY = 'ImporterWizardMethodExcelFile';

export const CONTACTS_OTHER_ORIGIN_ID = 'contacts_other';
export const OWNERS_OTHER_ORIGIN_ID = 'owners_other';

export const SPREADSHEET_ORIGIN_IDS = [
  CONTACTS_OTHER_ORIGIN_ID,
  OWNERS_OTHER_ORIGIN_ID,
];

export const isSpreadsheetOrigin = (originId) =>
  SPREADSHEET_ORIGIN_IDS.includes(originId);

export const PORTAL_FILE_METHOD_KEYS = [
  XML_METHOD_KEY,
  SQL_METHOD_KEY,
  EXCEL_METHOD_KEY,
];

export const isPortalFileMethod = (methodKey) =>
  PORTAL_FILE_METHOD_KEYS.includes(methodKey);

export const BRAZIL_COUNTRY_ISO_CODE = 'BR';
export const ARGENTINA_COUNTRY_ISO_CODE = 'AR';
export const MEXICO_COUNTRY_ISO_CODE = 'MX';
export const ECUADOR_COUNTRY_ISO_CODE = 'EC';
export const PERU_COUNTRY_ISO_CODE = 'PE';

export const EASYBROKER_ORIGIN_ID = 'easybroker';
export const EASYBROKER_FEATURE_FLAG = 'can_view_self_importer_easybroker';

export const isEasyBrokerOrigin = (originId) =>
  originId === EASYBROKER_ORIGIN_ID;

export const isEasyBrokerOriginVisible = (waffleFlags = []) =>
  waffleFlags.includes(EASYBROKER_FEATURE_FLAG);

const originOptions = [
  {
    id: 'vivareal',
    name: 'Vivareal',
    methodKey: XML_METHOD_KEY,
    logo: vivarealLogo,
    entity: 'properties',
    countries: [BRAZIL_COUNTRY_ISO_CODE],
  },
  {
    id: 'dfimoveis',
    name: 'DF Imóveis',
    methodKey: XML_METHOD_KEY,
    logo: dfImoveisLogo,
    entity: 'properties',
    countries: [BRAZIL_COUNTRY_ISO_CODE],
  },
  {
    id: 'imovelweb',
    name: 'Imovelweb',
    methodKey: API_METHOD_KEY,
    logo: imovelwebLogo,
    entity: 'properties',
    countries: [BRAZIL_COUNTRY_ISO_CODE],
  },
  {
    id: 'casa_mineira',
    name: 'Casa Mineira',
    methodKey: API_METHOD_KEY,
    logo: casaMineiraLogo,
    entity: 'properties',
    countries: [BRAZIL_COUNTRY_ISO_CODE],
  },
  {
    id: 'zonaprop',
    name: 'Zonaprop',
    methodKey: API_METHOD_KEY,
    logo: imovelwebLogo,
    entity: 'properties',
    countries: [ARGENTINA_COUNTRY_ISO_CODE],
  },
  {
    id: 'inmuebles24',
    name: 'Inmuebles 24',
    methodKey: API_METHOD_KEY,
    logo: imovelwebLogo,
    entity: 'properties',
    countries: [MEXICO_COUNTRY_ISO_CODE],
  },
  {
    id: 'easybroker',
    name: 'EasyBroker',
    methodKey: API_METHOD_KEY,
    icon: 'icon-empresa',
    entity: 'properties',
    isCrm: true,
    crmEntities: ['properties', 'contacts'],
    countries: [MEXICO_COUNTRY_ISO_CODE],
  },
  {
    id: 'plusvalia',
    name: 'Plusvalia',
    methodKey: API_METHOD_KEY,
    logo: imovelwebLogo,
    entity: 'properties',
    countries: [ECUADOR_COUNTRY_ISO_CODE],
  },
  {
    id: 'urbania',
    name: 'Urbania',
    methodKey: API_METHOD_KEY,
    logo: urbaniaLogo,
    entity: 'properties',
    countries: [PERU_COUNTRY_ISO_CODE],
  },
  {
    id: 'kenlo',
    name: 'Kenlo',
    methodKey: EXCEL_METHOD_KEY,
    icon: 'icon-empresa',
    entity: 'properties',
    isCrm: true,
    countries: [BRAZIL_COUNTRY_ISO_CODE],
  },
  {
    id: 'loft',
    name: 'Loft (previously Vista)',
    methodKey: SQL_METHOD_KEY,
    icon: 'icon-empresa',
    entity: 'properties',
    isCrm: true,
    countries: [BRAZIL_COUNTRY_ISO_CODE],
  },
  {
    id: CONTACTS_OTHER_ORIGIN_ID,
    nameKey: 'ImporterWizardOriginContacts',
    methodKey: EXCEL_METHOD_KEY,
    icon: 'icon-contactos',
    entity: 'contacts',
  },
  {
    id: OWNERS_OTHER_ORIGIN_ID,
    nameKey: 'ImporterWizardOriginPropertyOwners',
    methodKey: EXCEL_METHOD_KEY,
    icon: 'icon-propiedades',
    entity: 'owners',
  },
];

export const isOriginAvailableForCountry = (option, countryIsoCode) =>
  !option.countries || option.countries.includes(countryIsoCode);

export const getOriginOptionsForCountry = (
  countryIsoCode,
  { canViewEasyBroker = false } = {}
) =>
  originOptions.filter((option) => {
    if (option.id === EASYBROKER_ORIGIN_ID && !canViewEasyBroker) {
      return false;
    }
    return isOriginAvailableForCountry(option, countryIsoCode);
  });

export const findOriginOption = (originId) =>
  originOptions.find((item) => item.id === originId);

export const isApiPortalOrigin = (originId) => {
  const option = findOriginOption(originId);
  return option?.methodKey === API_METHOD_KEY && !option?.isCrm;
};

export const isCrmApiOrigin = (originId) => {
  const option = findOriginOption(originId);
  return option?.isCrm === true && option?.methodKey === API_METHOD_KEY;
};

export default originOptions;
